import { GridStack } from "gridstack";
import { NBadge, NButton, NInput, NPopselect, NSpin, NTooltip, SelectOption } from 'naive-ui';
import { mapState } from "pinia";
import { defineComponent, PropType } from "vue";

import { Station } from "~/api/models/station.model";
import { AvoltaAlarmConfiguration, AvoltaAlarmMapping } from "~/configs/alarms/avolta-alarm.config";
import { AvoltaAlarm, AvoltaAlarmEnum, AvoltaAlarmKeyEnum } from "~/configs/alarms/avolta-alarm.model";

import componentBase from "~/components/component-base";
import WidgetBase from '~/components/widget-base/widget-base.vue';
import { isStationInGasAlarm, isStationInTrouble, isStationOffline } from '~/misc/station-utils';
import { useDashboardStore } from "~/store/dashboard.store";

import DeviceListItem from "~/components/device-list-item/device-list-item.vue";
import { DashboardFilterEnum } from "~/models/dashboard/dashboard-filter.enum";
import { useStationStore } from "~/store/station.store";

export enum AvoltaAlarmStatus {
    OK = 'OK',
    TROUBLE = 'Trouble',
    ALARM = 'Alarm'
}

export default defineComponent({

    extends: componentBase,

    components: {
        WidgetBase,
        NPopselect,
        NButton,
        NInput,
        NTooltip,
        NBadge,
        NSpin,
        DeviceListItem,
    },

    props: {
        grid: { type: Object as PropType<GridStack | null> },
    },

    data() {
        return {
            search: '',
            AvoltaAlarmStatus,
            AvoltaAlarmEnum,
            DashboardFilterEnum,

            options: [
                { label: this.$t('global.all'), value: DashboardFilterEnum.ALL},
                { label: this.$t('global.ok'), value: DashboardFilterEnum.OK},
                { label: this.$t('avolta.alarm.ch4.short'), value: DashboardFilterEnum.GAS},
                { label: this.$t('global.trouble'), value: DashboardFilterEnum.TROUBLE},
                { label: this.$t('global.network.offline'), value: DashboardFilterEnum.OFFLINE},
            ] as SelectOption[],

            filter: DashboardFilterEnum.ALL,
        };
    },
    
    methods: {
        getAlarmIcon(alarm: AvoltaAlarmEnum): string {
            const config: AvoltaAlarm = AvoltaAlarmConfiguration[alarm];
            return config?.icon;
        },

        getAlarmTooltip(alarm: AvoltaAlarmEnum): string {
            const config: AvoltaAlarm = AvoltaAlarmConfiguration[alarm];
            return config?.short;
        },

        getStationStatus(station: Station): AvoltaAlarmStatus {
            const alarms = this.getStationAlerts(station);
            if(alarms.includes(AvoltaAlarmEnum.CH4_concentration_alarm)) {
                return AvoltaAlarmStatus.ALARM;
            } else if(alarms.length > 0 || station.status === 'offline') {
                return AvoltaAlarmStatus.TROUBLE;
            } else {
                return AvoltaAlarmStatus.OK;
            }
        },

        getStationAlerts(station: Station): AvoltaAlarmEnum[] {
            const result = Object.keys(station.latest_values)
                .map((key: string) => {
                    if (key in AvoltaAlarmKeyEnum) {
                        const alert = station.latest_values[key];
                        return alert ? AvoltaAlarmMapping[key] : null;
                    } else {
                        return null;
                    }
                })
                .filter((alarm: AvoltaAlarmEnum) => alarm !== AvoltaAlarmEnum.offline)
                .filter((alarm: AvoltaAlarmEnum) => alarm); // filter out null values
                
            return result;
        },

        toggleFilter(filter: DashboardFilterEnum) {
            this.filter = filter;
        }
    },

    computed: {

        ...mapState(useDashboardStore, ['since', 'before', 'loading']),
        ...mapState(useStationStore, ['stations']),

        filtered(): Station[] {
            
            if(!this.stations) {
                return [];
            }

            return this.stations
                .filter((device: Station) => device.status !== 'disabled')
                .filter((device: Station) => device.name.includes(this.search)) // filter by name
                .filter((device: Station) => {
                    switch(this.filter) {
                        case DashboardFilterEnum.ALL:
                            return true;
                        case DashboardFilterEnum.GAS:
                            return isStationInGasAlarm(device);
                        case DashboardFilterEnum.TROUBLE:
                            return isStationInTrouble(device);
                        case DashboardFilterEnum.OFFLINE:
                            return isStationOffline(device);
                        case DashboardFilterEnum.OK:
                            return !isStationInGasAlarm(device) && !isStationInTrouble(device) && !isStationOffline(device);
                        default:
                            return true;
                    }
                }) || [];

        },
    },
});